import { MAX_IMAGE_UPLOAD_BYTES } from '@/common/domain/utils/assertSafeImageUrl';
import { formatMaxUploadSize } from '@/common/presentation/utils/formatMaxUploadSize';
import type { HostUploadRules } from '@/host-config/domain/HostConfig';

export interface ImageUploadRules {
  maxFileSizeBytes: number;
  allowedMimeTypes: readonly string[];
  oversizeMessage?: string;
}

const DEFAULT_ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg'] as const;

/** Template Styles panel logo default (design spec: 'Max file size of 2 MB'). A host override still wins. */
export const LOGO_UPLOAD_MAX_BYTES = 2 * 1024 * 1024;

export function resolveImageUploadRules(uploads?: HostUploadRules, defaultMaxFileSizeBytes = MAX_IMAGE_UPLOAD_BYTES): ImageUploadRules {
  return {
    maxFileSizeBytes: uploads?.maxFileSizeBytes ?? defaultMaxFileSizeBytes,
    allowedMimeTypes: uploads?.allowedMimeTypes ?? DEFAULT_ALLOWED_MIME_TYPES,
    oversizeMessage: uploads?.oversizeMessage
  };
}

export function resolveLogoUploadRules(uploads?: HostUploadRules): ImageUploadRules {
  return resolveImageUploadRules(uploads, LOGO_UPLOAD_MAX_BYTES);
}

export function formatImageOversizeMessage(rules: ImageUploadRules): string {
  if (rules.oversizeMessage) {
    return rules.oversizeMessage;
  }

  return `Image must be ${formatMaxUploadSize(rules.maxFileSizeBytes)} or smaller`;
}

export function formatImageUploadHelperText(rules: ImageUploadRules, detail?: string): string {
  const sizeLabel = formatMaxUploadSize(rules.maxFileSizeBytes);
  const typeLabel = formatAllowedImageTypes(rules.allowedMimeTypes);
  if (!detail) {
    return `${typeLabel}, max ${sizeLabel}`;
  }

  if (detail.startsWith('(')) {
    return `${typeLabel}, max ${sizeLabel} ${detail}`;
  }

  return `${typeLabel}, max ${sizeLabel} — ${detail}`;
}

export function formatUnsupportedImageTypeMessage(rules: ImageUploadRules): string {
  const typeLabel = formatAllowedImageTypes(rules.allowedMimeTypes).replace(/ only$/i, '');
  if (typeLabel === 'Supported images') {
    return 'Only supported image types are allowed';
  }

  return `Only ${typeLabel} images are supported`;
}

function imageTypeLabels(allowedMimeTypes: readonly string[]): string[] {
  const labels = new Set<string>();
  for (const mime of allowedMimeTypes) {
    const normalized = mime.toLowerCase();
    if (normalized === 'image/png') labels.add('PNG');
    else if (normalized === 'image/jpeg' || normalized === 'image/jpg') labels.add('JPG');
    else if (normalized.startsWith('image/')) labels.add(normalized.slice('image/'.length).toUpperCase());
  }

  return Array.from(labels);
}

/** Two-line hint under a drop target: "Accepted formats: PNG, JPG" / "Max file size of 10 MB." */
export function formatImageDropTargetHint(rules: ImageUploadRules): string {
  const labels = imageTypeLabels(rules.allowedMimeTypes);
  const listed = labels.length > 0 ? labels.join(', ') : 'supported images';
  return `Accepted formats: ${listed}\nMax file size of ${formatMaxUploadSize(rules.maxFileSizeBytes)}.`;
}

function formatAllowedImageTypes(allowedMimeTypes: readonly string[]): string {
  const ordered = imageTypeLabels(allowedMimeTypes);
  if (ordered.length === 0) {
    return 'Supported images only';
  }

  if (ordered.length === 1) {
    return `${ordered[0]} only`;
  }

  if (ordered.length === 2) {
    return `${ordered[0]} or ${ordered[1]} only`;
  }

  return `${ordered.slice(0, -1).join(', ')}, or ${ordered[ordered.length - 1]} only`;
}
